import React, { useRef, useState } from 'react';
import { Icon, ICONS } from 'components/Icons';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';

const ACCEPTED_TYPES = ['.pdf', '.doc', '.docx', '.csv'];
const MAX_SIZE_MB = 10;

export default function SyllabusUploadStep1({
  onAnalyze,
  onSkip,
  onCancel,
  loading,
}) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const validateFile = (f) => {
    if (!f) return false;
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!ACCEPTED_TYPES.includes(ext)) {
      setError('Please upload a PDF, Word, or CSV file.');
      return false;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File must be under ${MAX_SIZE_MB}MB.`);
      return false;
    }
    setError('');
    return true;
  };

  const handleFile = (f) => {
    if (validateFile(f)) setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  return (
    <div className="syllabus-step">
      <div className="main-con">
        <div className="modal-header">
          <div className="syllabus-step-title">Upload your syllabus</div>
          <p className="syllabus-step-desc">
            Every Sunday, AverPoint will suggest news articles relevant to your
            weekly lectures. Please upload a syllabus that includes dates and
            lecture topics — or a simple table with columns for date and lecture
            topic.
          </p>
        </div>
        <div className='section-con'>
          {loading ? (
            <div className="syllabus-processing">
              <div className="processing-spinner"></div>
              <div className="processing-text">Processing your syllabus...</div>
              <div className="processing-subtext">This may take a few moments</div>
            </div>
          ) : (
            <div
              className={`syllabus-dropzone${dragOver ? ' drag-over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current && inputRef.current.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.doc,.docx,.csv"
                style={{ display: 'none' }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              <div className="dropzone-icon">
                <Icon icon={ICONS.UPLOAD_FILE} />
              </div>
              {file ? (
                <div className="dropzone-filename font-weight-600">
                  {file.name}
                </div>
              ) : (
                <div className="file-upload-title">Choose file to upload</div>
              )}
              <div className="dropzone-hint">
                PDF, Word, or CSV · max {MAX_SIZE_MB} MB
              </div>
              <div className="dropzone-browse av-blue cursor-pointer">
                Browse files
              </div>
            </div>
          )}
        </div>
        {error && <div className="syllabus-error mt-2">{error}</div>}

        <div className="syllabus-step-footer">
          <RectangularButton onClick={onCancel} disabled={loading}>Cancel</RectangularButton>
          <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
            <RectangularButton onClick={onSkip} disabled={loading}>Enter manually</RectangularButton>
            <RectangularButton
              darkButton
              disabled={!file || loading}
              onClick={() => onAnalyze(file)}
            >
              {loading ? 'Processing...' : 'Upload'}
            </RectangularButton>
          </div>
        </div>
      </div>
    </div>
  );
}
